<template>
  <div>
    <!-- 觸發 Modal 的按鈕 -->
    <button class="btn btn-green" @click="showModal">+ Import transaction file</button>

    <!-- 單一 Modal：根據 modalStep 切換畫面 -->
    <div class="modal fade" ref="staffMemberModal" tabindex="-1" role="dialog">
      <!-- 使 Modal 垂直置中：modal-dialog-centered -->
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content" style="min-height: 400px;">
          <div class="modal-body d-flex flex-column">
            
            <!-- 關閉按鈕，放在右上角 -->
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h5 class="import-title">Import transactions file</h5>
              <button type="button" class="close-btn" @click="hideModal">X</button>
            </div>

            <!-- Step 1: 上傳表單 -->
            <div v-if="modalStep === 'form'">
              <div class="card bg-file flex-grow-1 mb-3">
                <div class="card-body">
                  <div class="form-group">
                    <label for="file-import" class="file-import-text">File import</label>

                    <!-- 上傳區域 -->
                    <div
                      class="upload-content d-flex flex-column align-items-center justify-content-center"
                      :class="{ 'drag-over': isDraggingOver }"
                      @dragenter.prevent="handleDragEnter"
                      @dragover.prevent="handleDragOver"
                      @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleDrop"
                    >
                      <!-- 尚未選擇檔案時 -->
                      <template v-if="!selectedFile">
                        <div class="drag-text mb-3">
                          Drag and drop file or select manually
                        </div>
                        <input
                          ref="fileInput"
                          type="file"
                          style="display: none"
                          accept=".csv"
                          @change="handleFileChange"
                        />
                        <button class="btn btn-upload-file" @click="triggerFileSelect">
                          Select file
                        </button>
                      </template>

                      <!-- 已選擇檔案時 -->
                      <template v-else>
                        <!-- 根據副檔名動態顯示不同圖示 -->
                        <img
                          :src="fileIconSrc"
                          alt="File Icon"
                          class="preview-image mb-2"
                        />
                        <!-- 檔名 -->
                        <p class="file-name mb-2">{{ selectedFile.name }}</p>
                        <!-- 重新選擇檔案 -->
                        <button class="btn btn-upload-file" @click="handleReSelectFile">
                          Re-select file
                        </button>
                      </template>
                    </div>
                  </div>

                  <div class="import-note-text">
                    Note the file must be a CSV file. The name of the file should end with .csv 
                  </div>
                </div>
              </div>

              <!-- 上傳與取消按鈕 -->
              <div class="d-flex justify-content-end">
                <button class="btn btn-green btn-ok" :disabled="!isFormValid" @click="submitForm">Upload</button>
                <button class="btn ms-2 btn-cancel" @click="hideModal">Cancel</button>
              </div>
            </div>

            <!-- Step 2: Loading -->
            <div v-else-if="modalStep === 'loading'" class="flex-grow-1 d-flex flex-column align-items-center justify-content-center">
              <div class="spinner"></div>
              <div class="loading-text mt-3">Uploading...</div>
            </div>

            <!-- Step 3: 上傳成功 -->
            <div v-else-if="modalStep === 'success'" class="flex-grow-1 d-flex flex-column">
              <!-- 上方：綠色圖示 + 文字置中 -->
              <div class="flex-grow-1 d-flex align-items-center justify-content-center flex-wrap text-center">
                <div class="success-icon">✔</div>
                <h3 class="mt-3 ms-3">Upload successful</h3>
              </div>
              <!-- 下方：OK按鈕靠右下 -->
              <div class="d-flex justify-content-end">
                <button class="btn btn-green btn-ok mb-2" @click="hideModal">OK</button>
              </div>
            </div>

            <!-- Step 4: 上傳失敗 -->
            <div v-else-if="modalStep === 'failure'" class="flex-grow-1 d-flex flex-column">
              <!-- 上方：X 與「Upload unsuccessful」同一行 -->
              <div class="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center">
                <div class="d-flex align-items-center">
                  <div class="error-icon">✖</div>
                  <h3 class="ms-2 mb-0">Upload unsuccessful</h3>
                </div>
                <!-- 下方：錯誤提示文字 -->
                <p class="mt-2">Please check file and try again</p>
              </div>

              <!-- 下方：OK按鈕靠右下 -->
              <div class="d-flex justify-content-end">
                <button class="btn btn-green btn-ok mb-2" @click="hideModal">OK</button>
              </div>
            </div>

          </div> <!-- end of modal-body -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useTransactionStore } from '@/store/admin/transaction';
import { Modal } from 'bootstrap';

const transactionStore = useTransactionStore();
// 用來表示現在的 Modal 狀態
// 'form' -> 'loading' -> 'success' or 'failure'
const modalStep = ref('form');

const staffMemberModal = ref(null);
let modalInstance = null;

const showModal = () => {
  // 每次打開時，先設定到表單狀態
  modalStep.value = 'form';

  if (!modalInstance) {
    modalInstance = new Modal(staffMemberModal.value);
  }
  modalInstance.show();
};

const hideModal = () => {
  if (modalInstance) {
    selectedFile.value = null;
    fileContent.value = '';
    modalInstance.hide();
  }
};

// file input 相關
const fileInput = ref(null);
// 用來儲存當前選取的檔案
const selectedFile = ref(null);
// 用來儲存檔案內容 (讀取後)
const fileContent = ref('');

// 拖曳相關
const isDraggingOver = ref(false);

// 根據副檔名動態回傳對應 icon 圖檔路徑
const fileIconSrc = computed(() => {
  if (!selectedFile.value) {
    // 若未選擇檔案，可回傳預設圖示或空字串
    return '@/assets/images/icon/default.png'; 
  }

  // 取得檔名並轉成小寫
  const fileName = selectedFile.value.name.toLowerCase();

  const csvIconPath = require('@/assets/images/icon/csv.png');
  // 判斷後綴
  if (fileName.endsWith('.csv')) {
    return csvIconPath;
  } 
  // 如果有其他檔案類型可以再擴充
});

// 觸發 file input 選檔
const triggerFileSelect = () => {
  fileInput.value?.click();
};

// 處理檔案選擇(手動選檔)
const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  validateAndReadFile(file, event);
};

// 重新選擇檔案
const handleReSelectFile = () => {
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  selectedFile.value = null;
  fileContent.value = '';
};

// 拖曳事件處理
const handleDragEnter = () => {
  isDraggingOver.value = true;
};

const handleDragOver = () => {
  isDraggingOver.value = true;
};

const handleDragLeave = () => {
  isDraggingOver.value = false;
};

const handleDrop = (event) => {
  isDraggingOver.value = false;
  const droppedFiles = event.dataTransfer.files;
  if (!droppedFiles.length) return;

  // 只允許一次一個檔案
  if (droppedFiles.length > 1) {
    alert('一次僅能上傳一個檔案！');
    return;
  }
  const file = droppedFiles[0];
  validateAndReadFile(file);
};

// 驗證並讀取檔案 (支援手動與拖曳)
const validateAndReadFile = (file, event = null) => {
  // 檢查副檔名
  const allowedExtensions = ['.csv'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

  if (!hasValidExtension) {
    alert('只能上傳 .csv 檔案！');
    // 清空 input
    if (event) event.target.value = '';
    selectedFile.value = null;
    return;
  }

  // 檢查檔案大小 (2MB為範例)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    alert('檔案大小不得超過 2MB！');
    if (event) event.target.value = '';
    selectedFile.value = null;
    return;
  }

  // 通過檢查後，存下檔案，並讀取內容
  selectedFile.value = file;

  const reader = new FileReader();
  reader.onload = (e) => {
    fileContent.value = e.target.result;
    console.log('File content (preview):', fileContent.value);
  };
  reader.readAsText(file);
};

// 判斷是否可上傳
const isFormValid = computed(() => {
  return !!selectedFile.value;
});

// 上傳
const submitForm = async () => {
  if (!selectedFile.value) {
    alert('請先選擇檔案!');
    return;
  }

  modalStep.value = 'loading';

  try {
    const res = await transactionStore.uploadTransactions(selectedFile.value);
    
    if (res) {
      modalStep.value = 'success';
      transactionStore.fetchTransactions();
      selectedFile.value = null;
      fileContent.value = '';
    } else {
      modalStep.value = 'failure';
      selectedFile.value = null;
      fileContent.value = '';
    }
  } catch (error) {
    console.error(error);
    modalStep.value = 'failure';
    selectedFile.value = null;
    fileContent.value = '';
  }
};
</script>

<style scoped>
/* === 主要按鈕 === */
.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 278.83px;
  height: 46px;
  border-radius: 30px;
  padding: 10px 32px;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-ok {
  width: 150px;
}

.btn-cancel {
  background-color: #fff;
  color: black;
  width: 150px;
  height: 46px;
  border-radius: 30px;
  border: 1px solid #8476f4;
  padding: 10px 32px;
  margin-bottom: 10px;
}

.btn-cancel:hover {
  background-color: #e6e6ff;
}

/* === 關閉按鈕 === */
.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #333;
}
.close-btn:hover {
  color: #000;
}

/* === Modal === */
.modal-content {
  border-radius: 10px;
  padding: 20px;
  /* 固定最小高度以維持在 loading / success / failure 時大小一致 */
  min-height: 400px;
}

/* === 內容卡片 === */
.bg-file {
  background-color: #d9dde3;
}

.form-group {
  margin-bottom: 15px;
}

/* === File import 區域 === */
.file-import-text {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 23px;
  color: #051C2C;
}

.upload-content {
  width: 100%;
  height: 30vh;
  background-color: #fff;
  border-radius: 1%;
  transition: background-color 0.3s ease;
  border: 2px dashed #ccc;
  position: relative;
}

.upload-content.drag-over {
  background-color: #ecfff3; /* 拖曳時略顯示綠底 */
  border-color: #00c962;     /* 邊框變成綠色 */
}

.drag-text {
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #051C2C;
}

.btn-upload-file {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 14px 32px;
  gap: 10px;
  width: 200px;
  height: 46px;
  border: 2px solid #00C962;
  border-radius: 30px;
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  color: #051C2C;
  background-color: transparent;
  cursor: pointer;
}
.btn-upload-file:hover {
  background-color: #00C962;
  color: #fff;
}

.import-note-text {
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 19px;
  display: flex;
  align-items: center;
  color: #000000;
}

/* === 預設圖片、檔名 === */
.preview-image {
  width: 80px; /* 可自行調整 */
  height: auto;
  object-fit: contain;
}

.file-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

/* === Loading 狀態 === */
.spinner {
  width: 50px;
  height: 50px;
  border: 6px solid #ddd;
  border-top-color: #00c962;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 18px;
  font-family: 'Mulish';
  font-weight: 600;
  color: #051C2C;
}

/* === 成功或失敗時的圖示與文字 === */
.success-icon {
  font-size: 50px;
  color: #00c962; /* 綠色 */
}

.error-icon {
  font-size: 50px;
  color: red; /* 紅色 */
}
</style>
